/**
 * What a build will cost, said before the button rather than after it.
 *
 * Walked the web flow on a phone on 2026-09-25: six steps of choices, "Build this app", and
 * an anonymous visitor lands on a Create account screen — having never been told the price,
 * and never having been told an account was needed at all. The native app does better and
 * still not well: "This build uses 1,000 credits" appears only on the final step.
 *
 * One function, used by both clients, because the price a person is told is exactly the kind
 * of thing that drifts when each client writes its own sentence — and the two clients here
 * already share no publish code, which is how everyone's apps once ended up in Discover.
 *
 * It answers with a TONE as well as words, so a client can style "your first one is free"
 * differently from "you are short" without parsing the sentence to find out which it got.
 */

/*
 * Written out rather than imported, and pinned to the server by the test instead.
 *
 * The true source is `OPERATION_CREDITS` in infra/lambda/shared/credits.ts, and importing it
 * would be the obvious single source. But credits.ts imports models.ts — which reads
 * `process.env` at load and carries every model id and note the server uses — so one import
 * here would ship the server's model table into both client bundles, and this file is on
 * the web's boundary allowlist precisely because it imports nothing. The test compares these
 * to the server's table itself, so a server price change turns it red.
 */
/** The server's `OPERATION_CREDITS.generation`. A price shown must be the price charged. */
export const BUILD_COST = 1000;
/** The server's `OPERATION_CREDITS.edit`. */
export const EDIT_COST = 500;
/**
 * What tailoring a plan costs: `/plan` is charged in the `edit` category, and is free only
 * while the first build is unspent (webBuild.ts `planIsFree`). So after the free app, a NEW
 * app is this plus BUILD_COST — the whole journey, which is the only view a person has.
 */
export const PLAN_COST = EDIT_COST;
/** A new app after the free one: its plan and its build. */
const NEW_APP_COST = PLAN_COST + BUILD_COST;

export type CostTone =
  /** Signed out: the account is the ask, and the first build is the reason to make one. */
  | 'invite'
  /** This build is free and we are sure of it. */
  | 'free'
  /** It costs, and they can afford it. */
  | 'cost'
  /** It costs more than they have — said while they can still do something about it. */
  | 'short'
  /** Nothing honest to say: unlimited, or the server has not answered yet. */
  | 'silent';

export interface CostNote {
  tone: CostTone;
  headline: string;
  detail: string;
}

export interface CostInput {
  signedIn: boolean;
  tier?: 'free' | 'paid' | 'unlimited';
  creditsRemaining?: number;
  /** undefined means NOT KNOWN — deliberately distinct from false. */
  firstBuildDone?: boolean;
  /**
   * The caller can keep this screen's choices across a top-up. Absent means NO.
   *
   * True on the phone, whose paywall is a screen pushed over the wizard and whose draft is
   * still there when it closes. False in the browser: checkout is a full-page navigation to
   * Stripe, the draft lives in memory, and the person came back to an empty /new having been
   * told "everything you have chosen here is kept". A client has to claim this, so a new
   * caller that forgets makes no promise instead of a false one.
   */
  topUpKeepsChoices?: boolean;
}

const n = (v: number) => v.toLocaleString();
/** "1,500 credits (500 to plan, 1,000 to build)" — quoted where the plan leg is still ahead. */
const newAppPrice = () => `${n(NEW_APP_COST)} credits (${n(PLAN_COST)} to plan, ${n(BUILD_COST)} to build)`;
const SILENT: CostNote = { tone: 'silent', headline: '', detail: '' };

export function buildCostNote(input: CostInput): CostNote {
  /*
   * Signed out first, because this is the case both clients got wrong and it is the only one
   * where the answer is not about money at all. Saying it HERE, next to the button, is the
   * minimum; saying it before six steps of work would be better still.
   */
  if (!input.signedIn) {
    return {
      tone: 'invite',
      headline: 'Your first app is free',
      detail: `You'll make a free account to build it — after that a new app is ${newAppPrice()} and a change is ${n(EDIT_COST)}.`,
    };
  }

  // Nothing to bill, so nothing to say. A price note on an unlimited account is noise.
  if (input.tier === 'unlimited') return SILENT;

  /*
   * Quiet rather than guessing. `firstBuildDone: undefined` means the server has not told us,
   * which is not the same as false — and promising a free build we are not sure of is worse
   * than saying nothing, because the person acts on it.
   */
  if (input.firstBuildDone === undefined || input.creditsRemaining === undefined) return SILENT;

  if (!input.firstBuildDone) {
    return {
      tone: 'free',
      headline: 'This one is free',
      detail: `Your first build costs nothing. After it, a new app is ${newAppPrice()} and a change is ${n(EDIT_COST)}.`,
    };
  }

  if (input.creditsRemaining < BUILD_COST) {
    return {
      tone: 'short',
      headline: `This build needs ${n(BUILD_COST)} credits`,
      detail: input.topUpKeepsChoices
        ? `You have ${n(input.creditsRemaining)}. Top up and everything you have chosen here is kept.`
        : `You have ${n(input.creditsRemaining)}. Top up to build it.`,
    };
  }

  return {
    tone: 'cost',
    headline: `This build uses ${n(BUILD_COST)} credits`,
    detail: `You have ${n(input.creditsRemaining)}. Changing it afterwards costs ${n(EDIT_COST)} a time.`,
  };
}
